import React, { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";

import { formatRecipientName } from "../../utils/letterBoard";
import { getSendableRecipients } from "../../utils/letterSend";
import LetterDialog, { LetterDialogActions, letterModalBodyClass } from "../UX/LetterDialog";

function SendNowModal({ open = true, onClose, onConfirm, recipients = [] }) {
  const sendableRecipients = useMemo(
    () => getSendableRecipients(recipients),
    [recipients]
  );

  const [selectedIds, setSelectedIds] = useState(() =>
    sendableRecipients.map((recipient) => recipient._id)
  );

  useEffect(() => {
    setSelectedIds(sendableRecipients.map((recipient) => recipient._id));
  }, [sendableRecipients]);

  const allSelected = selectedIds.length === sendableRecipients.length;
  const selectedCount = selectedIds.length;

  const toggleRecipient = (recipientId) => {
    setSelectedIds((prev) =>
      prev.includes(recipientId)
        ? prev.filter((id) => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  const toggleAll = () => {
    setSelectedIds(
      allSelected ? [] : sendableRecipients.map((recipient) => recipient._id)
    );
  };

  const handleConfirm = () => {
    if (!selectedIds.length) return;
    onConfirm(selectedIds);
  };

  return (
    <LetterDialog
      open={open}
      onClose={onClose}
      centered
      maxWidth="max-w-xl"
      scrollable
      animationVariant="scaleFade"
      title="Veux-tu envoyer cette lettre maintenant ?"
      footer={
        <LetterDialogActions
          secondaryLabel="Plus tard"
          onSecondary={onClose}
          secondaryVariant="outline-purple"
          primaryLabel={
            selectedCount === sendableRecipients.length
              ? "Envoyer à tous"
              : `Envoyer (${selectedCount})`
          }
          onPrimary={handleConfirm}
          primaryVariant="purple"
          primaryDisabled={!selectedCount}
        />
      }
    >
      <p className={`${letterModalBodyClass} text-center`}>
        Choisis les destinataires qui recevront un exemplaire physique de cette
        lettre dans les prochains jours. Assure-toi que leurs informations sont
        correctes avant de confirmer l&apos;envoi.
      </p>

      <div className="mb-6">
        <div className="mb-3 flex items-center justify-between">
          <p className="font-josefin text-sm font-medium text-secondaryText">
            Destinataires
          </p>
          {sendableRecipients.length > 1 && (
            <button
              type="button"
              onClick={toggleAll}
              className="font-josefin text-sm text-brandPurple transition hover:text-primaryDark"
            >
              {allSelected ? "Tout désélectionner" : "Tout sélectionner"}
            </button>
          )}
        </div>

        <ul className="flex flex-col gap-2">
          {sendableRecipients.map((recipient) => {
            const isSelected = selectedIds.includes(recipient._id);

            return (
              <li key={recipient._id}>
                <button
                  type="button"
                  onClick={() => toggleRecipient(recipient._id)}
                  className={`flex w-full items-center gap-3 rounded-md border px-4 py-3 text-left transition ${
                    isSelected
                      ? "border-brandPurple bg-brandPurple/5"
                      : "border-greyLight bg-white hover:border-greyLight/80"
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition ${
                      isSelected
                        ? "border-brandPurple bg-brandPurple text-white"
                        : "border-greyLight bg-white"
                    }`}
                  >
                    {isSelected && <Check size={12} strokeWidth={3} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-josefin text-sm font-medium text-secondaryText">
                      {formatRecipientName(recipient)}
                    </span>
                    {(recipient.city || recipient.country) && (
                      <span className="block truncate font-josefin text-xs text-textMuted">
                        {[recipient.city, recipient.country].filter(Boolean).join(", ")}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </LetterDialog>
  );
}

export default SendNowModal;
